import type { ManifestFragment } from "@/app/integration/contracts";
import { toMp3FallbackPath } from "@/audio/resource-paths";
import type { LevelDefinition } from "@/domain";
import { isLocalResourceReference } from "@/domain/schemas";

import {
  classifyPublicResourcePath,
  manifestFragmentSchema
} from "./schema";

export type AssetValidationErrorCode =
  | "invalid-resource-reference"
  | "invalid-manifest-fragment"
  | "duplicate-manifest-owner"
  | "unknown-level-id"
  | "missing-file"
  | "undeclared-level-reference";

export interface AssetValidationError {
  readonly code: AssetValidationErrorCode;
  readonly levelId: string;
  readonly path: string;
  readonly message: string;
}

export interface LevelResourceReference {
  readonly levelId: string;
  readonly path: string;
  readonly fieldPath: string;
  readonly kind: "audio" | "image";
}

export interface ContentAssetValidationInput {
  readonly levels: readonly LevelDefinition[];
  readonly manifestFragments: readonly unknown[];
  readonly availablePublicPaths: ReadonlySet<string>;
}

export type ContentAssetValidationResult =
  | {
      readonly success: true;
      readonly references: readonly LevelResourceReference[];
      readonly fragments: readonly ManifestFragment[];
      readonly errors: readonly [];
    }
  | {
      readonly success: false;
      readonly references: readonly LevelResourceReference[];
      readonly fragments: readonly ManifestFragment[];
      readonly errors: readonly AssetValidationError[];
    };

export class AssetValidationFailure extends Error {
  readonly errors: readonly AssetValidationError[];

  constructor(errors: readonly AssetValidationError[]) {
    super(errors.map(formatAssetValidationError).join("\n"));
    this.name = "AssetValidationFailure";
    this.errors = errors;
  }
}

function formatPath(path: readonly PropertyKey[]): string {
  if (path.length === 0) {
    return "<root>";
  }

  return path
    .map((segment, index) => {
      if (typeof segment === "number") {
        return "[" + segment + "]";
      }
      return index === 0 ? String(segment) : "." + String(segment);
    })
    .join("");
}

export function formatAssetValidationError(
  error: AssetValidationError
): string {
  return error.levelId + " " + error.path + ": " + error.message;
}

function isResourceField(field: PropertyKey | undefined): boolean {
  return (
    typeof field === "string" &&
    /(?:audio|asset|assets|src)$/iu.test(field)
  );
}

interface CollectedReferenceCandidate {
  readonly path: string;
  readonly fieldPath: string;
}

function collectNestedReferenceCandidates(
  value: unknown,
  path: readonly PropertyKey[],
  candidates: CollectedReferenceCandidate[],
  seen: WeakSet<object>
): void {
  if (typeof value === "string") {
    const parentField = path[path.length - 1];
    if (
      value.startsWith("/assets/") ||
      value.startsWith("/audio/") ||
      isResourceField(parentField)
    ) {
      candidates.push({ path: value, fieldPath: formatPath(path) });
    }
    return;
  }

  if (typeof value !== "object" || value === null || seen.has(value)) {
    return;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      collectNestedReferenceCandidates(
        entry,
        [...path, index],
        candidates,
        seen
      );
    });
    return;
  }

  for (const [key, entry] of Object.entries(value)) {
    collectNestedReferenceCandidates(
      entry,
      [...path, key],
      candidates,
      seen
    );
  }
}

export function collectLevelResourceReferences(
  levels: readonly LevelDefinition[]
): {
  readonly references: readonly LevelResourceReference[];
  readonly errors: readonly AssetValidationError[];
} {
  const references: LevelResourceReference[] = [];
  const errors: AssetValidationError[] = [];

  for (const level of levels) {
    const candidates: CollectedReferenceCandidate[] = [];

    if (level.promptAudio !== undefined) {
      candidates.push({
        path: level.promptAudio,
        fieldPath: "promptAudio"
      });
    }

    level.assets.forEach((path, index) => {
      candidates.push({ path, fieldPath: "assets[" + index + "]" });
    });

    collectNestedReferenceCandidates(
      level.payload,
      ["payload"],
      candidates,
      new WeakSet<object>()
    );

    for (const candidate of candidates) {
      const kind = classifyPublicResourcePath(candidate.path);
      if (
        kind === undefined ||
        !isLocalResourceReference(candidate.path)
      ) {
        errors.push({
          code: "invalid-resource-reference",
          levelId: level.id,
          path: candidate.fieldPath,
          message:
            "Expected a local /assets/ or /audio/ path, received " +
            candidate.path
        });
        continue;
      }

      references.push({
        levelId: level.id,
        path: candidate.path,
        fieldPath: candidate.fieldPath,
        kind
      });

      if (kind === "audio" && candidate.path.endsWith(".ogg")) {
        references.push({
          levelId: level.id,
          path: toMp3FallbackPath(candidate.path),
          fieldPath: candidate.fieldPath,
          kind
        });
      }
    }
  }

  return { references, errors };
}

function manifestOwner(value: unknown, index: number): string {
  if (
    typeof value === "object" &&
    value !== null &&
    "owner" in value &&
    typeof value.owner === "string" &&
    value.owner.length > 0
  ) {
    return value.owner;
  }
  return "fragment-" + index;
}

function manifestIssueLevelId(
  fragment: unknown,
  issuePath: readonly PropertyKey[],
  owner: string
): string {
  if (
    issuePath[0] !== "requirements" ||
    typeof issuePath[1] !== "number" ||
    typeof fragment !== "object" ||
    fragment === null ||
    !("requirements" in fragment) ||
    !Array.isArray(fragment.requirements)
  ) {
    return "<manifest:" + owner + ">";
  }

  const requirement = fragment.requirements[issuePath[1]];
  if (
    typeof requirement === "object" &&
    requirement !== null &&
    "levelIds" in requirement &&
    Array.isArray(requirement.levelIds)
  ) {
    const levelId = requirement.levelIds.find(
      (candidate: unknown): candidate is string =>
        typeof candidate === "string" && candidate.length > 0
    );
    if (levelId !== undefined) {
      return levelId;
    }
  }

  return "<manifest:" + owner + ">";
}

function parseManifestFragments(
  inputs: readonly unknown[],
  errors: AssetValidationError[]
): ManifestFragment[] {
  const fragments: ManifestFragment[] = [];
  const seenOwners = new Set<string>();

  inputs.forEach((input, index) => {
    const owner = manifestOwner(input, index);
    const result = manifestFragmentSchema.safeParse(input);

    if (!result.success) {
      for (const issue of result.error.issues) {
        errors.push({
          code: "invalid-manifest-fragment",
          levelId: manifestIssueLevelId(input, issue.path, owner),
          path: formatPath(["manifestFragments", index, ...issue.path]),
          message: issue.message
        });
      }
      return;
    }

    if (seenOwners.has(result.data.owner)) {
      errors.push({
        code: "duplicate-manifest-owner",
        levelId: "<manifest:" + result.data.owner + ">",
        path: "manifestFragments[" + index + "].owner",
        message: "Manifest fragment owner is registered more than once"
      });
    }
    seenOwners.add(result.data.owner);
    fragments.push(result.data);
  });

  return fragments;
}

function firstReferenceByLevelAndPath(
  references: readonly LevelResourceReference[]
): Map<string, LevelResourceReference> {
  const firstReferences = new Map<string, LevelResourceReference>();

  for (const reference of references) {
    const key = reference.levelId + "\u0000" + reference.path;
    if (!firstReferences.has(key)) {
      firstReferences.set(key, reference);
    }
  }

  return firstReferences;
}

export function validateContentAssets({
  levels,
  manifestFragments,
  availablePublicPaths
}: ContentAssetValidationInput): ContentAssetValidationResult {
  const collected = collectLevelResourceReferences(levels);
  const errors = [...collected.errors];
  const fragments = parseManifestFragments(manifestFragments, errors);
  const levelsById = new Map(levels.map((level) => [level.id, level]));
  const firstReferences = firstReferenceByLevelAndPath(collected.references);
  const declaredPathsByLevel = new Map<string, Set<string>>();

  for (const reference of firstReferences.values()) {
    if (!availablePublicPaths.has(reference.path)) {
      errors.push({
        code: "missing-file",
        levelId: reference.levelId,
        path: reference.fieldPath,
        message: "Missing public file " + reference.path
      });
    }
  }

  fragments.forEach((fragment, fragmentIndex) => {
    fragment.requirements.forEach((requirement, requirementIndex) => {
      requirement.levelIds.forEach((levelId, levelIdIndex) => {
        if (!levelsById.has(levelId)) {
          errors.push({
            code: "unknown-level-id",
            levelId,
            path:
              "manifestFragments[" + fragmentIndex +
              "].requirements[" + requirementIndex +
              "].levelIds[" + levelIdIndex + "]",
            message: "Manifest requirement references an unknown level"
          });
          return;
        }

        const declared = declaredPathsByLevel.get(levelId) ?? new Set<string>();
        declared.add(requirement.path);
        declaredPathsByLevel.set(levelId, declared);
      });

      if (!availablePublicPaths.has(requirement.path)) {
        for (const levelId of requirement.levelIds) {
          errors.push({
            code: "missing-file",
            levelId,
            path:
              "manifestFragments[" + fragmentIndex +
              "].requirements[" + requirementIndex + "].path",
            message: "Missing public file " + requirement.path
          });
        }
      }
    });
  });

  for (const reference of firstReferences.values()) {
    const declared = declaredPathsByLevel.get(reference.levelId);
    if (!declared?.has(reference.path)) {
      errors.push({
        code: "undeclared-level-reference",
        levelId: reference.levelId,
        path: reference.fieldPath,
        message:
          "Manifest fragments do not declare " + reference.path
      });
    }
  }

  return errors.length === 0
    ? {
        success: true,
        references: collected.references,
        fragments,
        errors: []
      }
    : {
        success: false,
        references: collected.references,
        fragments,
        errors
      };
}

export function parseContentAssets(
  input: ContentAssetValidationInput
): {
  readonly references: readonly LevelResourceReference[];
  readonly fragments: readonly ManifestFragment[];
} {
  const result = validateContentAssets(input);
  if (!result.success) {
    throw new AssetValidationFailure(result.errors);
  }

  return {
    references: result.references,
    fragments: result.fragments
  };
}
